import config from 'shared/config'
import logger from 'shared/logger'
import type { AutocompleteString } from '@precis-digital/kurama/utils'
import { type EventStatus, type TransformationType } from 'src/biddingSignals/types/dagView'
import { type WebVitalsMetricName } from './webVitals'
const log = logger().child({
  namespace: 'googleTagManager'
})

export const GTM_ID = 'GTM-NBZJ3N4'

type gtmEventId =
  | 'run_now_click'
  | 'run_full_refresh_click'
  | 'create_dashboard_click'
  | 'recommendations_popup_open'
  | 'csv_export'
  | 'chart_export'
  | 'global_filters_update'
  | 'channel_results_popup_open'
  | WebVitalsMetricName
  | 'promo_banner_interaction'
  | 'dag_view_open'
  | 'bs_transformation_accordion_toggle'
  | 'bs_target_accordion_toggle'
  | 'unexpected_error'
  | 'api_error'
type gtmProductType =
  | 'connectors'
  | 'reporting_solutions'
  | 'attribution_models'
  | 'budget_optimiser'
  | 'enhanced_signals'
  | 'home'
  | 'recommendations'
  | 'creative_reporting'
interface BaseGTMEvent {
  event: gtmEventId
  product?: gtmProductType
}

interface RunNowClick extends BaseGTMEvent {
  event: 'run_now_click'
  product: 'connectors' | 'reporting_solutions'
}

interface RunFullRefreshClick extends BaseGTMEvent {
  event: 'run_full_refresh_click'
  product: 'connectors'
}

interface CreateDashboardClick extends BaseGTMEvent {
  event: 'create_dashboard_click'
  product: 'reporting_solutions'
}

interface RecommendationsPopupOpen extends BaseGTMEvent {
  event: 'recommendations_popup_open'
  product: 'recommendations'
  recommendation_rule_id: string
}

/** Tracks user interactions with promo banners across the application.
 * Use this event for all promo banner interactions (learn more, dismiss, snooze).
 * @param banner_id - The ID of the promo banner (e.g., 'attributionOnHomePage')
 * @param action - The action taken by the user
 * @param location - Optional location/place where the banner is displayed (e.g., 'home_page', 'conversion_events_flyout')
 */
interface PromoBannerInteraction extends BaseGTMEvent {
  event: 'promo_banner_interaction'
  banner_id: string
  action: 'learn_more' | 'dismiss' | 'snooze'
  location?: string
}

interface WebVitalsEvent extends BaseGTMEvent {
  event: WebVitalsMetricName
  metric_name: WebVitalsMetricName
  metric_value: number
  metric_delta?: number
  metric_rating?: 'good' | 'needs-improvement' | 'poor'
  navigation_type?: 'navigate' | 'reload' | 'back_forward' | 'prerender'
}

interface CsvExportEvent extends BaseGTMEvent {
  event: 'csv_export'
  product: 'attribution_models' | 'home' | 'budget_optimiser' | 'creative_reporting' | 'enhanced_signals'
}

interface ChartExportEvent {
  event: 'chart_export'
  format: 'svg' | 'png'
}

interface GlobalFiltersApplyEvent extends BaseGTMEvent {
  event: 'global_filters_update'
  product: 'attribution_models'
  filters: string
}

interface ChannelResultsPopupOpenEvent extends BaseGTMEvent {
  event: 'channel_results_popup_open'
  attribution_config_id: number
  channel_id: string
}

interface DagViewOpen extends BaseGTMEvent {
  event: 'dag_view_open'
  product: gtmProductType
  event_status: EventStatus | AutocompleteString
}

interface BsTransformationAccordionToggle extends BaseGTMEvent {
  event: 'bs_transformation_accordion_toggle'
  product: 'enhanced_signals'
  transformation_type: TransformationType
}

interface BsTargetAccordionToggle extends BaseGTMEvent {
  event: 'bs_target_accordion_toggle'
  product: 'enhanced_signals'
  target_id: string
}

interface UnexpectedErrorEvent {
  event: 'unexpected_error'
  error_message: string
  page_path: string
  timestamp: string
}

interface ApiErrorEvent {
  event: 'api_error'
  endpoint: string
  status_code: number
  error_message: string
  page_path: string
  timestamp: string
}

type GTMEvent =
  | RunNowClick
  | RunFullRefreshClick
  | CreateDashboardClick
  | RecommendationsPopupOpen
  | WebVitalsEvent
  | CsvExportEvent
  | ChartExportEvent
  | GlobalFiltersApplyEvent
  | ChannelResultsPopupOpenEvent
  | PromoBannerInteraction
  | DagViewOpen
  | BsTransformationAccordionToggle
  | BsTargetAccordionToggle
  | UnexpectedErrorEvent
  | ApiErrorEvent

declare const window: Window & { dataLayer: GTMEvent[] }

export const sendGTMEvent = (event: GTMEvent): void => {
  if (config('APP_ENV') !== 'production' || typeof window.dataLayer === 'undefined') {
    return
  }
  try {
    window.dataLayer.push(event)
  } catch (e) {
    log.error(`Error sending GTM event: ${(e as Error).message}`)
  }
}
